import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DepButton } from '@pkg/components/deprecated';
import { TwoFactorAuthType } from '@pkg/types/enums';

import { Auth } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';

interface AuthSetupAuthenticatorProps {
    onNext: () => void;
}

export const AuthSetupAuthenticator = ({ onNext }: AuthSetupAuthenticatorProps) => {
    const [qrCode, setQrCode] = useState('');
    const [qrSvg, setQrSvg] = useState('');
    const { t } = useTranslation(['twoFactorAuth']);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const formattedQrCode = useMemo(() => {
        return qrCode.match(/.{1,4}/g)?.join(' ');
    }, [qrCode]);

    const handleCopyQrCode = useCallback(() => {
        if (!qrCode) {
            return;
        }
        const el = document.createElement('textarea');
        el.value = qrCode;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopied(true);
    }, [qrCode]);

    useEffect(() => {
        const provider = TwoFactorAuthType.GoogleAuthenticator;

        setLoading(true);
        apiFactory()
            .call(Auth.enable2FA, { value: { provider, default: true } })
            .then((res) => {
                if (res.ok) {
                    setQrCode(res.value.ga_secret || '');
                    setQrSvg(res.value.ga_qr_code_svg || '');
                    setLoading(false);
                }
            });
    }, []);

    useEffect(() => {
        if (!copied) {
            return;
        }
        const timeOut = setTimeout(() => {
            setCopied(false);
        }, 3000);

        return () => {
            clearTimeout(timeOut);
        };
    }, [copied]);

    return (
        <>
            <h2
                className="two-factor-auth-step__title mb-small"
                dangerouslySetInnerHTML={{ __html: t('2fa_step_setup_authenticator_title') }}
            />
            <p
                className="two-factor-auth-step__detail"
                dangerouslySetInnerHTML={{ __html: t('2fa_step_setup_authenticator_detail') }}
            ></p>
            {!!qrCode && (
                <>
                    <div className="two-factor-auth-step__qrcode" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                    <div className={`two-factor-auth-step__code${copied ? ' copied' : ''}`}>
                        <small>Authenticator Code</small>
                        <span className="two-factor-auth-step__code-code">{formattedQrCode}</span>
                        <span
                            className={`two-factor-auth-step__code-action${copied ? ' copied' : ''}`}
                            onClick={handleCopyQrCode}
                        >
                            {copied ? t('2fa_text_copied') : t('2fa_text_copy')}
                        </span>
                    </div>
                    <p
                        className={`two-factor-auth-step__code-action--xs${copied ? ' copied' : ''}`}
                        onClick={handleCopyQrCode}
                    >
                        {copied ? t('2fa_text_copied') : t('2fa_text_copy_long')}
                    </p>
                </>
            )}
            <DepButton classes="two-factor-auth-step__action" onClick={onNext} loading={loading}>
                Next
            </DepButton>
        </>
    );
};
